import { omit } from 'lodash';
import { call, put, select } from 'typed-redux-saga';

import { emotions } from '../../../../../shared/constants';
import { LoggableError } from '../../../../../shared/helpers/errors';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { Emotion } from '../../../../../types';
import { isNonCriticalFetchError } from '../../../helpers/handlers-sagas/handle-fetch-error';
import { getConferenceConfig, getMyUserId, selectConference } from '../../../selectors';
import { updateParticipant } from '../../conference/slice';
import { setEmotionAction } from '../slice';

const logger = createLogger('goloom:emotion');

export function* onEmotionSaga(emotion: Emotion) {
    const { peerId } = emotion;
    const { participantsByIds, participants } = yield* select(selectConference);
    const participant = participantsByIds[peerId];

    if (!participant) {
        logger.error(
            new LoggableError('An emoji was received from a participant who is not in the store', {
                method: 'onEmotionSaga',
                additional: { emotion, participants },
            })
        );

        return;
    }

    if (!emotions.includes(emotion.emotionType)) {
        logger.error(
            new LoggableError('An unknown emotionType was received', {
                method: 'onEmotionSaga',
                additional: { emotion, knownEmotions: emotions },
            })
        );

        return;
    }

    const now = Date.now();

    if (emotion.showDeadline < now) {
        logger.error(
            new LoggableError('The emoji was received late. The show deadline has already passed', {
                method: 'onEmotionSaga',
                additional: { emotion, now },
            })
        );
    }

    yield* put(updateParticipant({ id: peerId, emotion: omit(emotion, 'peerId') }));
}

export function* setEmotionSaga({ payload }: ReturnType<typeof setEmotionAction>) {
    const conferenceConfig = yield* select(getConferenceConfig);
    const myUserId = yield* select(getMyUserId);

    if (!conferenceConfig || !myUserId) {
        return;
    }

    try {
        yield* call([cloudApi, cloudApi.setPeerEmotion], conferenceConfig.resolved_uri, myUserId, {
            emotion_type: payload,
        });
    } catch (error) {
        logger.error(error, {
            level: isNonCriticalFetchError(error) ? 'warning' : 'error',
            method: 'setEmotionSaga',
        });
    }
}
